import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Shield, RefreshCcw, Loader2, UserCheck, UserX, Check, X, Trash2, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function AdminPage() {
  const { user, revalidate } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [busyPayIds, setBusyPayIds] = useState(new Set());
  const [busyDelIds, setBusyDelIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // user obj or null
  const [wipeAntifraudOpen, setWipeAntifraudOpen] = useState(false);
  const [wipeEmail, setWipeEmail] = useState("");
  const [wipePhrase, setWipePhrase] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | disabled | paid | unpaid
  const [sortKey, setSortKey] = useState("created_at"); // email|role|has_paid|is_active|created_at
  const [sortDir, setSortDir] = useState("desc"); // asc|desc

  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      // Server denies access → cookie now belongs to a non-admin user.
      // Force AuthContext to re-fetch /auth/me and bounce to the correct page.
      if (err.response?.status === 401 || err.response?.status === 403) {
        setForbidden(true);
        try { await revalidate?.(); } catch {}
        toast.error("La sessione non ha più i permessi di amministratore");
        return;
      }
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always re-check /admin/users at mount so a stale in-memory user (e.g. after
    // another tab replaced the cookie) is caught even if is_super_admin is still true.
    if (user?.is_super_admin) load();
  }, [user?.is_super_admin]);

  const toggle = async (u) => {
    if (u.is_super_admin) {
      toast.error("L'account super-admin non può essere disabilitato.");
      return;
    }
    setBusyIds((prev) => new Set([...prev, u.id]));
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/status`, {
        is_active: !u.is_active,
      });
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, is_active: data.is_active } : x))
      );
      toast.success(
        data.is_active ? `${u.email} attivato` : `${u.email} disattivato`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(u.id);
        return next;
      });
    }
  };

  const togglePayment = async (u) => {
    setBusyPayIds((prev) => new Set([...prev, u.id]));
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/payment`, {
        has_paid: !u.has_paid,
      });
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, has_paid: data.has_paid } : x))
      );
      toast.success(
        data.has_paid ? `${u.email} contrassegnato come pagato` : `${u.email} contrassegnato come non pagato`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyPayIds((prev) => {
        const next = new Set(prev);
        next.delete(u.id);
        return next;
      });
    }
  };

  const doDelete = async (u) => {
    setBusyDelIds((prev) => new Set([...prev, u.id]));
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      const d = data?.deleted || {};
      const parts = [];
      if (d.templates) parts.push(`${d.templates} template`);
      if (d.payment_transactions) parts.push(`${d.payment_transactions} pagamenti`);
      if (d.password_reset_tokens) parts.push(`${d.password_reset_tokens} token`);
      const extra = parts.length ? ` (${parts.join(", ")})` : "";
      toast.success(`${u.email} eliminato${extra}`);
      if (data?.stripe_errors?.length) {
        toast.warning(`Attenzione Stripe: ${data.stripe_errors.join("; ")}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyDelIds((prev) => {
        const next = new Set(prev);
        next.delete(u.id);
        return next;
      });
      setConfirmDelete(null);
    }
  };

  const openAuditLog = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const { data } = await api.get("/admin/audit-log?limit=100");
      setAuditItems(data?.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAuditLoading(false);
    }
  };

  const doWipeAntifraud = async () => {
    const email = (wipeEmail || "").trim().toLowerCase();
    const phrase = (wipePhrase || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Inserisci un'email valida");
      return;
    }
    const expected = `AZZERA PROVA ${email}`;
    if (phrase !== expected) {
      toast.error(`Devi scrivere esattamente: ${expected}`);
      return;
    }
    setWipeBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-antifraud", {
        email,
        confirm_phrase: phrase,
      });
      if (data?.deleted_count === 1) {
        toast.success(`Antifrode azzerato per ${email}. Potrà ricevere una nuova prova di 7 giorni.`);
      } else {
        toast.info(`Nessun record antifrode trovato per ${email}.`);
      }
      setWipeAntifraudOpen(false);
      setWipeEmail("");
      setWipePhrase("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setWipeBusy(false);
    }
  };

  // Guard: redirect immediately if the current session is not the super-admin.
  // This runs on every render, so the moment /auth/me returns a different user
  // (e.g. because another tab replaced the cookie) we bounce out of /admin
  // before any admin data is fetched or rendered.
  if (forbidden || (user && !user.is_super_admin)) {
    return <Navigate to="/" replace />;
  }
  // Still loading /auth/me: don't reveal the admin shell yet.
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050B1F] text-white/50 font-geist-mono text-xs">
        verifica sessione…
      </div>
    );
  }

  const activeCount = users.filter((u) => u.is_active).length;
  const paidCount = users.filter((u) => u.has_paid).length;

  const q = query.trim().toLowerCase();
  const filteredUsers = users.filter((u) => {
    if (q) {
      const hay = `${u.email || ""} ${u.name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filter === "active" && !u.is_active) return false;
    if (filter === "disabled" && u.is_active) return false;
    if (filter === "paid" && !u.has_paid) return false;
    if (filter === "unpaid" && u.has_paid) return false;
    return true;
  });

  const sortValue = (u) => {
    switch (sortKey) {
      case "email":
        return (u.email || "").toLowerCase();
      case "role":
        return u.is_super_admin ? "0-super" : u.role || "zzz";
      case "has_paid":
        return u.has_paid ? 1 : 0;
      case "trial": {
        // On trial if: Stripe trialing (legacy) OR free trial lato backend
        //   (no status + trial_end futuro + !has_paid).
        // Allineato con la logica di rendering e con has_access del backend.
        const te = u.trial_end ? new Date(u.trial_end).getTime() : 0;
        const future = te > Date.now();
        const isStripeTrial = u.subscription_status === "trialing" && future;
        const isFreeTrial = !u.subscription_status && future && !u.has_paid;
        return (isStripeTrial || isFreeTrial) ? 1 : 0;
      }
      case "is_active":
        return u.is_active ? 1 : 0;
      case "created_at":
      default: {
        const raw = u.created_at || "";
        const t = raw ? new Date(raw).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      }
    }
  };
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const av = sortValue(a);
    const bv = sortValue(b);
    let cmp;
    if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Sensible defaults: date desc (newest first), text asc, bool desc (paid/active first)
      setSortDir(key === "email" || key === "role" ? "asc" : "desc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };
  const headerBtnClass = (column) =>
    `flex items-center gap-1 uppercase tracking-[0.15em] transition-colors ${
      sortKey === column ? "text-[#4D8AFF]" : "text-white/40 hover:text-white/70"
    }`;

  const formatShortDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (!Number.isFinite(d.getTime())) return "—";
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
    } catch { return "—"; }
  };

  const filterChips = [
    { key: "all", label: "tutti" },
    { key: "active", label: "attivi" },
    { key: "disabled", label: "disattivi" },
    { key: "paid", label: "pagati" },
    { key: "unpaid", label: "non pagati" },
  ];

  return (
    <AppShell subtitle="admin · gestione utenti">
      <div className="max-w-6xl mx-auto p-8 md:p-12" data-testid="admin-page">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 font-geist-mono text-[10px] tracking-widest uppercase text-[#4D8AFF]">
              <Shield className="h-3 w-3" /> Admin dashboard
            </div>
            <h1 className="font-geist text-3xl md:text-4xl font-semibold mt-3 text-white">
              Utenti registrati
            </h1>
            <p className="mt-2 font-geist-mono text-[11px] text-white/40">
              {activeCount}/{users.length} attivi · {paidCount}/{users.length} pagato · accesso riservato a{" "}
              <span className="text-white/70">{user?.email}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-audit-log-btn"
              onClick={openAuditLog}
              variant="ghost"
              className="rounded-sm text-white/60 hover:text-white hover:bg-white/[0.06] font-geist-mono text-[11px] uppercase tracking-wider"
              title="Storico operazioni super-admin (elimina, azzera antifrode)"
            >
              Audit log
            </Button>
            <Button
              data-testid="admin-wipe-antifraud-btn"
              onClick={() => setWipeAntifraudOpen(true)}
              variant="ghost"
              className="rounded-sm text-amber-300/80 hover:text-amber-200 hover:bg-amber-400/10 font-geist-mono text-[11px] uppercase tracking-wider"
              title="Azzera lo storico antifrode per una singola email, consentendole una nuova prova di 7 giorni"
            >
              <Shield className="h-3.5 w-3.5 mr-1" />
              Azzera antifrode
            </Button>
            <Button
              data-testid="admin-refresh-btn"
              onClick={load}
              disabled={loading}
              variant="ghost"
              className="rounded-sm text-white/70 hover:text-white hover:bg-white/[0.06] font-geist-mono text-[11px] uppercase tracking-wider"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}
              aggiorna
            </Button>
          </div>
        </div>

        {error && (
          <div
            data-testid="admin-error"
            className="mb-6 font-geist-mono text-[11px] text-red-300 border border-red-400/30 bg-red-500/10 rounded-sm px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40 pointer-events-none" />
            <Input
              data-testid="admin-search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per email o nome…"
              className="pl-9 h-10 rounded-sm bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/30 font-geist-mono text-[12px] focus-visible:ring-[#4D8AFF] focus-visible:border-[#4D8AFF]/50"
            />
            {query && (
              <button
                data-testid="admin-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-sm flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06]"
                aria-label="Pulisci ricerca"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="admin-filter-chips">
            {filterChips.map((c) => {
              const active = filter === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`admin-filter-${c.key}`}
                  onClick={() => setFilter(c.key)}
                  className={`px-2.5 py-1 rounded-sm font-geist-mono text-[10px] uppercase tracking-wider border transition-colors ${
                    active
                      ? "bg-[#4D8AFF] text-[#050B1F] border-[#4D8AFF]"
                      : "text-white/60 border-white/[0.1] hover:text-white hover:border-white/30"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div
            className="font-geist-mono text-[10px] uppercase tracking-widest text-white/40 md:ml-auto"
            data-testid="admin-result-count"
          >
            {filteredUsers.length} / {users.length} risultati
          </div>
        </div>

        <div className="rounded-md border border-white/[0.08] bg-[#0A1738]/40 overflow-hidden">
          <div className="grid grid-cols-[minmax(0,1.5fr)_90px_100px_100px_110px_110px_130px_80px] px-4 py-3 border-b border-white/[0.08] font-geist-mono text-[10px]">
            <button
              type="button"
              onClick={() => toggleSort("email")}
              data-testid="admin-sort-email"
              className={headerBtnClass("email")}
            >
              Email / Nome <SortIcon column="email" />
            </button>
            <button
              type="button"
              onClick={() => toggleSort("role")}
              data-testid="admin-sort-role"
              className={headerBtnClass("role")}
            >
              Ruolo <SortIcon column="role" />
            </button>
            <button
              type="button"
              onClick={() => toggleSort("has_paid")}
              data-testid="admin-sort-payment"
              className={`${headerBtnClass("has_paid")} justify-center`}
            >
              Pagamento <SortIcon column="has_paid" />
            </button>
            <button
              type="button"
              onClick={() => toggleSort("is_active")}
              data-testid="admin-sort-status"
              className={`${headerBtnClass("is_active")} justify-center`}
            >
              Stato <SortIcon column="is_active" />
            </button>
            <button
              type="button"
              onClick={() => toggleSort("trial")}
              data-testid="admin-sort-trial"
              className={`${headerBtnClass("trial")} justify-center`}
            >
              Periodo di prova <SortIcon column="trial" />
            </button>
            <button
              type="button"
              onClick={() => toggleSort("created_at")}
              data-testid="admin-sort-created"
              className={headerBtnClass("created_at")}
            >
              Registrato <SortIcon column="created_at" />
            </button>
            <div className="text-right uppercase tracking-[0.15em] text-white/40">Azione</div>
            <div className="text-right uppercase tracking-[0.15em] text-white/40">Elimina</div>
          </div>

          {loading && users.length === 0 ? (
            <div className="p-8 text-center font-geist-mono text-xs text-white/40">
              <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin text-[#4D8AFF]" />
              caricamento…
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center font-geist-mono text-xs text-white/40" data-testid="admin-empty">
              {users.length === 0
                ? "Nessun utente"
                : `Nessun risultato per "${query}"${filter !== "all" ? ` con filtro "${filterChips.find((c) => c.key === filter)?.label}"` : ""}`}
            </div>
          ) : (
            sortedUsers.map((u) => {
              const busy = busyIds.has(u.id);
              return (
                <div
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  className="grid grid-cols-[minmax(0,1.5fr)_90px_100px_100px_110px_110px_130px_80px] items-center px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{u.email}</div>
                    <div className="font-geist-mono text-[10px] text-white/40 truncate">
                      {u.name || "—"}
                    </div>
                  </div>
                  <div className="font-geist-mono text-[11px] uppercase tracking-wider">
                    {u.is_super_admin ? (
                      <span className="text-[#4D8AFF]">super-admin</span>
                    ) : u.role === "admin" ? (
                      <span className="text-[#4D8AFF]/80">admin</span>
                    ) : (
                      <span className="text-white/50">user</span>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      data-testid={`admin-payment-btn-${u.id}`}
                      onClick={() => togglePayment(u)}
                      disabled={busyPayIds.has(u.id)}
                      title={u.has_paid ? "Clicca per contrassegnare come NON pagato" : "Clicca per contrassegnare come PAGATO"}
                      className={`inline-flex items-center justify-center h-7 w-7 rounded-sm transition-colors ${
                        u.has_paid
                          ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-400/25"
                          : "bg-red-500/10 text-red-300 border border-red-400/25 hover:bg-red-500/20"
                      }`}
                    >
                      {busyPayIds.has(u.id) ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : u.has_paid ? (
                        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                      ) : (
                        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                  <div className="text-center">
                    <span
                      data-testid={`admin-user-status-${u.id}`}
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm font-geist-mono text-[10px] uppercase tracking-wider ${
                        u.is_active
                          ? "bg-emerald-400/10 text-emerald-300 border border-emerald-400/20"
                          : "bg-red-500/10 text-red-300 border border-red-400/25"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          u.is_active ? "bg-emerald-400" : "bg-red-400"
                        }`}
                      />
                      {u.is_active ? "attivo" : "disattivo"}
                    </span>
                  </div>
                  <div className="text-center" data-testid={`admin-user-trial-${u.id}`}>
                    {(() => {
                      const te = u.trial_end ? new Date(u.trial_end).getTime() : 0;
                      const future = te > Date.now();
                      // Riconosce entrambi i casi:
                      // - trial Stripe (status="trialing")
                      // - free trial lato backend (no status + trial_end futuro)
                      const isStripeTrial = u.subscription_status === "trialing" && future;
                      const isFreeTrial = !u.subscription_status && future && !u.has_paid;
                      const onTrial = isStripeTrial || isFreeTrial;
                      const endLabel = u.trial_end
                        ? new Date(u.trial_end).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })
                        : null;
                      return (
                        <span
                          title={onTrial && endLabel ? `Scade il ${endLabel}` : undefined}
                          className={`inline-flex items-center px-2 py-0.5 rounded-sm font-geist-mono text-[10px] uppercase tracking-wider ${
                            onTrial
                              ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                              : "bg-white/[0.03] text-white/50 border border-white/[0.08]"
                          }`}
                        >
                          {onTrial ? (endLabel || "Sì") : "No"}
                        </span>
                      );
                    })()}
                  </div>
                  <div
                    className="font-geist-mono text-[11px] text-white/60"
                    title={u.created_at || ""}
                    data-testid={`admin-user-created-${u.id}`}
                  >
                    {formatShortDate(u.created_at)}
                  </div>
                  <div className="text-right">
                    <Button
                      data-testid={`admin-toggle-btn-${u.id}`}
                      onClick={() => toggle(u)}
                      disabled={busy || u.is_super_admin}
                      size="sm"
                      variant="ghost"
                      className={`rounded-sm font-geist-mono text-[11px] uppercase tracking-wider h-8 ${
                        u.is_active
                          ? "text-red-300 hover:bg-red-500/10 hover:text-red-200"
                          : "text-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200"
                      }`}
                      title={u.is_super_admin ? "Il super-admin non può essere disabilitato" : ""}
                    >
                      {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : u.is_active ? (
                        <><UserX className="h-3.5 w-3.5 mr-1" /> disattiva</>
                      ) : (
                        <><UserCheck className="h-3.5 w-3.5 mr-1" /> attiva</>
                      )}
                    </Button>
                  </div>
                  <div className="text-right">
                    <Button
                      data-testid={`admin-delete-btn-${u.id}`}
                      onClick={() => setConfirmDelete(u)}
                      disabled={busyDelIds.has(u.id) || u.is_super_admin || u.id === user?.id}
                      size="sm"
                      variant="ghost"
                      title={
                        u.is_super_admin
                          ? "Il super-admin non può essere eliminato"
                          : u.id === user?.id
                          ? "Non puoi eliminare il tuo stesso account"
                          : "Elimina utente e tutti i suoi dati"
                      }
                      className="rounded-sm h-8 text-red-300 hover:bg-red-500/10 hover:text-red-200 disabled:opacity-30 font-geist-mono text-[11px] uppercase tracking-wider"
                    >
                      {busyDelIds.has(u.id) ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <p className="mt-4 font-geist-mono text-[10px] text-white/40 leading-relaxed">
          Nota: disabilitando un utente, i suoi token attivi vengono immediatamente
          rifiutati. L'account super-admin è protetto e non può essere disabilitato.
        </p>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent
          data-testid="admin-delete-confirm-dialog"
          className="bg-[#0A1738] border border-white/[0.08] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-geist text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Elimina utente
            </AlertDialogTitle>
            <AlertDialogDescription className="font-geist-mono text-[11px] text-white/70 leading-relaxed pt-2">
              Sei sicuro di voler eliminare <span className="text-white">{confirmDelete?.email}</span>?
              L'operazione è irreversibile: verranno cancellati definitivamente l'account, tutti i
              template salvati, la cronologia dei pagamenti e le informazioni di abbonamento
              (l'eventuale abbonamento Stripe attivo verrà annullato).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-delete-cancel"
              className="rounded-sm bg-transparent border-white/[0.15] text-white/80 hover:bg-white/[0.06] hover:text-white font-geist-mono text-[11px] uppercase tracking-wider"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              onClick={(e) => { e.preventDefault(); if (confirmDelete) doDelete(confirmDelete); }}
              disabled={confirmDelete && busyDelIds.has(confirmDelete.id)}
              className="rounded-sm bg-red-500 hover:bg-red-600 text-white font-geist font-semibold"
            >
              {confirmDelete && busyDelIds.has(confirmDelete.id) ? (
                <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Eliminazione…</span>
              ) : (
                "Elimina definitivamente"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={wipeAntifraudOpen} onOpenChange={(open) => { if (!open) { setWipeAntifraudOpen(false); setWipeEmail(""); setWipePhrase(""); } }}>
        <AlertDialogContent
          data-testid="admin-wipe-antifraud-dialog"
          className="bg-[#0A1328] border-amber-400/30 text-white rounded-sm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-geist text-xl text-amber-200">
              Azzera antifrode per una email
            </AlertDialogTitle>
            <AlertDialogDescription className="font-geist text-sm text-white/70 leading-relaxed">
              Questa operazione cancella il record dello storico antifrode <code className="text-amber-300">email_trial_state</code> per la singola email indicata.
              <br />Alla prossima registrazione, l'utente riceverà <strong className="text-amber-200">una nuova prova gratuita di 7 giorni</strong>.
              <br /><br />
              <strong className="text-red-300">Attenzione:</strong> l'account applicativo deve essere stato <u>già eliminato</u> dalla lista utenti. Altrimenti l'operazione viene rifiutata (409).
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 mt-2">
            <div>
              <label className="font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50">Email target</label>
              <Input
                data-testid="admin-wipe-email-input"
                type="email"
                value={wipeEmail}
                onChange={(e) => setWipeEmail(e.target.value)}
                placeholder="esempio@dominio.it"
                className="mt-1 bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                Conferma: scrivi <code className="text-amber-300">AZZERA PROVA {wipeEmail.trim().toLowerCase() || "<email>"}</code>
              </label>
              <Input
                data-testid="admin-wipe-phrase-input"
                value={wipePhrase}
                onChange={(e) => setWipePhrase(e.target.value)}
                placeholder={`AZZERA PROVA ${wipeEmail.trim().toLowerCase() || "<email>"}`}
                className="mt-1 bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist-mono text-xs"
                autoComplete="off"
              />
            </div>
          </div>

          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              data-testid="admin-wipe-cancel"
              disabled={wipeBusy}
              className="rounded-sm bg-transparent border-white/[0.15] text-white/80 hover:bg-white/[0.06] hover:text-white font-geist-mono text-[11px] uppercase tracking-wider"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-wipe-confirm"
              onClick={(e) => { e.preventDefault(); doWipeAntifraud(); }}
              disabled={wipeBusy || !wipeEmail || !wipePhrase}
              className="rounded-sm bg-amber-500 hover:bg-amber-600 text-[#050B1F] font-geist font-semibold"
            >
              {wipeBusy ? (
                <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Azzeramento…</span>
              ) : (
                "Azzera antifrode"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={auditOpen} onOpenChange={(open) => { if (!open) setAuditOpen(false); }}>
        <AlertDialogContent
          data-testid="admin-audit-log-dialog"
          className="bg-[#0A1328] border-white/[0.15] text-white rounded-sm max-w-3xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-geist text-xl text-white">Audit log super-admin</AlertDialogTitle>
            <AlertDialogDescription className="font-geist-mono text-[11px] text-white/50">
              Ultime 100 operazioni ({auditItems.length} caricate). Scorri per vedere i dettagli.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-[60vh] overflow-y-auto space-y-2 mt-2">
            {auditLoading && (
              <div className="flex items-center justify-center py-8 text-white/50 font-geist-mono text-xs">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> caricamento…
              </div>
            )}
            {!auditLoading && auditItems.length === 0 && (
              <div className="text-center py-8 font-geist-mono text-xs text-white/40">
                Nessuna operazione registrata.
              </div>
            )}
            {!auditLoading && auditItems.map((it) => (
              <div
                key={it.id}
                data-testid="admin-audit-item"
                className="border border-white/[0.1] rounded-sm px-3 py-2 bg-white/[0.02] font-geist text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={
                    it.action === "delete_user"
                      ? "font-geist-mono text-[10px] uppercase tracking-wider text-red-300 bg-red-500/10 border border-red-400/30 rounded-sm px-2 py-0.5"
                      : it.action === "wipe_antifraud"
                      ? "font-geist-mono text-[10px] uppercase tracking-wider text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-sm px-2 py-0.5"
                      : "font-geist-mono text-[10px] uppercase tracking-wider text-white/60 bg-white/[0.04] border border-white/[0.1] rounded-sm px-2 py-0.5"
                  }>
                    {it.action}
                  </span>
                  <span className="font-geist-mono text-[10px] text-white/40">
                    {it.executed_at ? new Date(it.executed_at).toLocaleString("it-IT") : "—"}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-3 flex-wrap text-xs">
                  <span className="text-white/70"><span className="text-white/40">target:</span> {it.email_target || "—"}</span>
                  {it.email_key_prefix && (
                    <span className="font-geist-mono text-white/50"><span className="text-white/40">ek:</span> {it.email_key_prefix}…</span>
                  )}
                  <span className="font-geist-mono text-white/50"><span className="text-white/40">by:</span> {it.admin_email || "—"}</span>
                </div>
                {(it.deleted || it.deleted_count !== undefined) && (
                  <div className="mt-1 font-geist-mono text-[10px] text-white/50">
                    {it.deleted && Object.entries(it.deleted).map(([k, v]) => `${k}=${v}`).join(" · ")}
                    {it.deleted_count !== undefined && ` deleted_count=${it.deleted_count}`}
                    {it.pre_source && ` · pre_source=${it.pre_source}`}
                  </div>
                )}
              </div>
            ))}
          </div>

          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              data-testid="admin-audit-close"
              className="rounded-sm bg-transparent border-white/[0.15] text-white/80 hover:bg-white/[0.06] hover:text-white font-geist-mono text-[11px] uppercase tracking-wider"
            >
              Chiudi
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
